import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import SessionTimeout from "@/components/SessionTimeout";
import { isAdminPortal, isPartnerPortal, isQrZonePortal } from "@/lib/domain";
import { encodeAdminPath, resolveAdminPath, shouldObfuscateRoutes } from "@/lib/adminRoutes";
import { installScreenshotDeterrent, setWatermarkIdentity } from "@/lib/screenshotDeterrent";
import AnalyticsProvider from "@/components/AnalyticsProvider";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import ImpersonateLanding from "@/pages/ImpersonateLanding";
import PaymentCheckout from "@/pages/PaymentCheckout";
import PartnerPaymentLinks from "@/pages/PartnerPaymentLinks";
import KycPage from "@/pages/Kyc";
import BecomeRetailer from "@/pages/BecomeRetailer";
import Dashboard from "@/pages/Dashboard";
import Wallet from "@/pages/Wallet";
import Beneficiaries from "@/pages/Beneficiaries";
import BbpsFlow from "@/pages/BbpsFlow";
import BbpsV2Wizard from "@/pages/BbpsV2Wizard";
import Payouts from "@/pages/Payouts";
import KycLinks from "@/pages/KycLinks";
import OnboardKyc from "@/pages/OnboardKyc";
import GiftStorefront from "@/pages/GiftStorefront";
import PendingCollections from "@/pages/PendingCollections";
import Transactions from "@/pages/Transactions";
import Packages from "@/pages/Packages";
import Reports from "@/pages/Reports";
import AdminKycQueue from "@/pages/admin/AdminKyc";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminBeneficiaryLibrary from "@/pages/admin/AdminBeneficiaryLibrary";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import AdminAllTxns from "@/pages/admin/AdminAllTxns";
import AdminApprovalDetail from "@/pages/admin/AdminApprovalDetail";
import AdminBeneficiaryApprovals from "@/pages/admin/AdminBeneficiaryApprovals";
import AdminRetailers from "@/pages/admin/AdminRetailers";
import AdminFeatureMatrix from "@/pages/admin/AdminFeatureMatrix";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminSubAdmins from "@/pages/admin/AdminSubAdmins";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerKyc from "@/pages/admin/AdminCustomerKyc";
import AdminCustomerWalletRules from "@/pages/admin/AdminCustomerWalletRules";
import AdminWebhookEvents from "@/pages/admin/AdminWebhookEvents";
import AdminOutboundPayments from "@/pages/admin/AdminOutboundPayments";
import AdminOutboundTxns from "@/pages/admin/AdminOutboundTxns";
import AdminQaCenter from "@/pages/admin/AdminQaCenter";
import AdminActivityLog from "@/pages/admin/AdminActivityLog";
import AdminPaymentLinks from "@/pages/admin/AdminPaymentLinks";
import PaymentLinkDetail from "@/pages/admin/PaymentLinkDetail";
import AdminApiTester from "@/pages/admin/AdminApiTester";
import AdminSignupReport from "@/pages/admin/AdminSignupReport";
import AdminApiLogs from "@/pages/admin/AdminApiLogs";
import AdminSdkClients from "@/pages/admin/AdminSdkClients";
import AdminPlatformOverview from "@/pages/admin/AdminPlatformOverview";
import AdminDomains from "@/pages/admin/AdminDomains";
import AdminPaymentModes from "@/pages/admin/AdminPaymentModes";
import AdminWalletTxns from "@/pages/admin/AdminWalletTxns";
import AdminSystemTest from "@/pages/admin/AdminSystemTest";
import AdminStuckDebits from "@/pages/admin/AdminStuckDebits";
import AdminReconciliation from "@/pages/admin/AdminReconciliation";
import AdminTopupFees from "@/pages/admin/AdminTopupFees";
import AdminLandingServices from "@/pages/admin/AdminLandingServices";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminMenuConfig from "@/pages/admin/AdminMenuConfig";
import AdminChannels from "@/pages/admin/AdminChannels";
import AdminPaymentAnalytics from "@/pages/admin/AdminPaymentAnalytics";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminRiskRules from "@/pages/admin/AdminRiskRules";
import AdminEducationCategories from "@/pages/admin/AdminEducationCategories";
import ChannelDashboard from "@/pages/ChannelDashboard";
import CustomerKycWizard from "@/pages/CustomerKycWizard";
import CustomerWallet from "@/pages/CustomerWallet";

/** Partner & customer routes (rentpay.me) */
const PartnerRoutes = () => (
  <>
    <Route path="/" element={<Landing />} />
    <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
    <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
    <Route path="/impersonate" element={<ImpersonateLanding />} />
    <Route path="/pay/:linkId" element={<PaymentCheckout />} />
    <Route path="/p/:linkId" element={<PaymentCheckout />} />
    <Route path="/kyc" element={<ProtectedRoute><KycPage /></ProtectedRoute>} />
    <Route path="/become-retailer" element={<ProtectedRoute requireKyc><BecomeRetailer /></ProtectedRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute requireKyc><Dashboard /></ProtectedRoute>} />
    <Route path="/wallet" element={<ProtectedRoute requireKyc><Wallet /></ProtectedRoute>} />
    <Route path="/beneficiaries" element={<ProtectedRoute requireKyc><Beneficiaries /></ProtectedRoute>} />
    <Route path="/bbps" element={<ProtectedRoute requireKyc><BbpsFlow /></ProtectedRoute>} />
    <Route path="/bbps/v2" element={<ProtectedRoute requireKyc><BbpsV2Wizard /></ProtectedRoute>} />
    <Route path="/payouts" element={<ProtectedRoute requireKyc><Payouts /></ProtectedRoute>} />
    <Route path="/education-fees" element={<ProtectedRoute requireKyc><Payouts /></ProtectedRoute>} />
    <Route path="/kyc-links" element={<ProtectedRoute requireKyc><KycLinks /></ProtectedRoute>} />
    <Route path="/onboard/:token" element={<OnboardKyc />} />
    <Route path="/gifts" element={<GiftStorefront />} />
    <Route path="/gifts/:slug" element={<GiftStorefront />} />
    <Route path="/gifts/orders" element={<GiftStorefront />} />
    <Route path="/payouts/pending-collections" element={<ProtectedRoute requireKyc><PendingCollections /></ProtectedRoute>} />
    <Route path="/transactions" element={<ProtectedRoute requireKyc><Transactions /></ProtectedRoute>} />
    <Route path="/packages" element={<ProtectedRoute requireKyc><Packages /></ProtectedRoute>} />
    <Route path="/reports" element={<ProtectedRoute requireKyc><Reports /></ProtectedRoute>} />
    <Route path="/customer-kyc" element={<ProtectedRoute requireKyc><CustomerKycWizard /></ProtectedRoute>} />
    <Route path="/customer-wallet" element={<ProtectedRoute requireKyc><CustomerWallet /></ProtectedRoute>} />
    <Route path="/payment-links" element={<ProtectedRoute requireKyc><PartnerPaymentLinks /></ProtectedRoute>} />
    {/* Channel partner routes */}
    <Route path="/channel/dashboard" element={<ProtectedRoute><ChannelDashboard /></ProtectedRoute>} />
    <Route path="/channel/sub-partners" element={<ProtectedRoute><ChannelDashboard /></ProtectedRoute>} />
    <Route path="/channel/transactions" element={<ProtectedRoute><ChannelDashboard /></ProtectedRoute>} />
    <Route path="/channel/earnings" element={<ProtectedRoute><ChannelDashboard /></ProtectedRoute>} />
    {/* Catch admin routes on partner domain → redirect to login */}
    <Route path="/admin/*" element={<Navigate to="/login" replace />} />
  </>
);

/** Admin route definitions — maps real paths to components + access rules */
const ADMIN_ROUTE_DEFS = [
  { path: "/admin", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminApprovals },
  { path: "/admin/approvals", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminApprovals },
  { path: "/admin/all-txns", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminAllTxns },
  { path: "/admin/approvals/:txnId", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminApprovalDetail },
  { path: "/admin/beneficiary-approvals", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminBeneficiaryApprovals },
  { path: "/admin/kyc", role: "admin", comp: AdminKycQueue },
  { path: "/admin/retailers", role: "admin", comp: AdminRetailers },
  { path: "/admin/users", role: "admin", comp: AdminUsers },
  { path: "/admin/packages", role: "admin", comp: AdminPackages },
  { path: "/admin/beneficiary-library", role: "admin", comp: AdminBeneficiaryLibrary },
  { path: "/admin/integrations", role: "admin", comp: AdminIntegrations },
  { path: "/admin/feature-matrix", role: "admin", comp: AdminFeatureMatrix },
  { path: "/admin/roles", role: "admin", comp: AdminRoles },
  { path: "/admin/sub-admins", role: "admin", comp: AdminSubAdmins },
  { path: "/admin/customers", role: "admin", comp: AdminCustomers },
  { path: "/admin/customer-kyc", role: "admin", comp: AdminCustomerKyc },
  { path: "/admin/customer-wallet-rules", role: "admin", comp: AdminCustomerWalletRules },
  { path: "/admin/webhook-events", role: "admin", comp: AdminWebhookEvents },
  { path: "/admin/outbound-payments", role: "admin", comp: AdminOutboundPayments },
  { path: "/admin/outbound-txns", roles: ["admin","approver_l1","approver_l2","approver_l3","approver_all"], comp: AdminOutboundTxns },
  { path: "/admin/qa", role: "admin", comp: AdminQaCenter },
  { path: "/admin/activity-log", role: "admin", comp: AdminActivityLog },
  { path: "/admin/payment-links", role: "admin", comp: AdminPaymentLinks },
  { path: "/admin/payment-links/:linkId", role: "admin", comp: PaymentLinkDetail },
  { path: "/admin/api-tester", role: "admin", comp: AdminApiTester },
  { path: "/admin/signup-report", role: "admin", comp: AdminSignupReport },
  { path: "/admin/api-logs", role: "admin", comp: AdminApiLogs },
  { path: "/admin/sdk-clients", role: "admin", comp: AdminSdkClients },
  { path: "/admin/platform", role: "admin", comp: AdminPlatformOverview },
  { path: "/admin/domains", role: "admin", comp: AdminDomains },
  { path: "/admin/payment-modes", role: "admin", comp: AdminPaymentModes },
  { path: "/admin/wallet-txns", role: "admin", comp: AdminWalletTxns },
  { path: "/admin/stuck-debits", role: "admin", comp: AdminStuckDebits },
  { path: "/admin/reconciliation", role: "admin", comp: AdminReconciliation },
  { path: "/admin/topup-fees", role: "admin", comp: AdminTopupFees },
  { path: "/admin/landing-services", role: "admin", comp: AdminLandingServices },
  { path: "/admin/analytics", role: "admin", comp: AdminAnalytics },
  { path: "/admin/menu-config", role: "admin", comp: AdminMenuConfig },
  { path: "/admin/channels", role: "admin", comp: AdminChannels },
  { path: "/admin/payment-analytics", role: "admin", comp: AdminPaymentAnalytics },
  { path: "/admin/master-data", role: "admin", comp: AdminMasterData },
  { path: "/admin/risk-rules", role: "admin", comp: AdminRiskRules },
  { path: "/admin/education-categories", role: "admin", comp: AdminEducationCategories },
  { path: "/admin/system-test", role: "admin", comp: AdminSystemTest },
];

/** Build route elements — on admin portal, register both encoded AND real paths */
const buildAdminRoutes = () => {
  const obf = shouldObfuscateRoutes();
  const routes = [];
  ADMIN_ROUTE_DEFS.forEach((def, i) => {
    const Comp = def.comp;
    const protection = def.roles
      ? <ProtectedRoute requireRoles={def.roles}><Comp /></ProtectedRoute>
      : <ProtectedRoute requireRole={def.role}><Comp /></ProtectedRoute>;

    // Always register the encoded path (primary on admin domain)
    if (obf) {
      const encodedPath = encodeAdminPath(def.path);
      routes.push(<Route key={`enc-${i}`} path={encodedPath} element={protection} />);
    }
    // Also register the real path (for internal redirects + dev/preview)
    routes.push(<Route key={`real-${i}`} path={def.path} element={protection} />);
  });
  return routes;
};

/** Admin / Approver / Sub-admin routes (dev.rentpay.me) */
const AdminRoutes = () => (
  <>
    <Route path="/" element={<Navigate to="/login" replace />} />
    <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
    {/* Public payment-link routes — links generated from admin portal still need to render. */}
    <Route path="/pay/:linkId" element={<PaymentCheckout />} />
    <Route path="/p/:linkId" element={<PaymentCheckout />} />
    {buildAdminRoutes()}
    {/* Catch partner routes on admin domain → redirect to login */}
    <Route path="/dashboard" element={<Navigate to="/login" replace />} />
    <Route path="/wallet" element={<Navigate to="/login" replace />} />
    <Route path="/signup" element={<Navigate to="/login" replace />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </>
);

function ScreenCaptureGuard() {
  // Mounts inside AuthProvider so we can stamp the watermark with the user identity.
  const { user } = useAuth();
  useEffect(() => { installScreenshotDeterrent(); }, []);
  useEffect(() => {
    setWatermarkIdentity(user ? `${user.email || user.mobile || user.id}` : "");
  }, [user]);
  return null;
}

/** QR Zone gift card storefront (theqrzone.com) — consumer-only domain */
const QrZoneRoutes = () => (
  <>
    <Route path="/" element={<Navigate to="/gifts" replace />} />
    <Route path="/gifts" element={<GiftStorefront />} />
    <Route path="/gifts/:slug" element={<GiftStorefront />} />
    <Route path="/gifts/orders" element={<GiftStorefront />} />
    {/* Public payment-link checkout still served on this domain */}
    <Route path="/pay/:linkId" element={<PaymentCheckout />} />
    <Route path="/p/:linkId" element={<PaymentCheckout />} />
    {/* Everything else (partner / admin paths) → bounce to storefront */}
    <Route path="*" element={<Navigate to="/gifts" replace />} />
  </>
);

function App() {
  const adminOnly = isAdminPortal();
  const qrZoneOnly = isQrZonePortal();
  const partnerOnly = isPartnerPortal();

  // Stamp document.title + favicon hint for QR Zone visitors.
  useEffect(() => {
    if (qrZoneOnly) {
      document.title = "QR Zone — Premium gift cards";
    }
  }, [qrZoneOnly]);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScreenCaptureGuard />
          <AnalyticsProvider />
          <Toaster position="top-right" richColors closeButton />
          <SessionTimeout />
          <Routes>
            {adminOnly ? (
              <>{AdminRoutes()}</>
            ) : qrZoneOnly ? (
              <>{QrZoneRoutes()}</>
            ) : partnerOnly ? (
              <>{PartnerRoutes()}</>
            ) : (
              /* Dev/preview: show everything */
              <>
                {PartnerRoutes()}
                {buildAdminRoutes()}
              </>
            )}
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
